import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';
import {observer} from 'mobx-react';
import PageLayoutTemplate from '@ui/templates/page-template/page-layout';
import {Button} from '@ui/atoms/button';
import {ArrowLeftIcon} from '@ui/atoms/icons';
import {useStoreContext} from '@store/store-context';
import {useQuery} from 'react-query';
import dayjs from 'dayjs';
import {groupTypeController} from '@pages/dashboard/admin/admin-components/admin-people/admin-group-type/admin-group-type-controller';
import {groupTypeRequestImpl} from '@pages/dashboard/admin/admin-components/admin-people/admin-group-type/admin-group-type-request';
import {groupController} from '@pages/dashboard/admin/admin-components/admin-people/admin-group/admin-group-controller';
import {groupRequestImpl} from '@pages/dashboard/admin/admin-components/admin-people/admin-group/admin-group-request';
import {
  CadenceStep,
  ContentStep,
  IncludeScope,
  ExcludeScope,
  BasicsStep,
  VisibilityOption,
  PreviewStep,
  PreviewSection,
  WizardSteps,
} from './components';
import {
  CadenceFormState,
  ContentBlock,
  CreateCheckinCycleFormData,
  DayOfWeek,
  SelectOption,
} from './components/types';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {ICheckinCycleDetail} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {ItemLoader} from '@ui/organisms/item-loader';
import {
  cloneFormData,
  hasFormDataChanged,
} from './components/review-form-state';
import {
  getBasicsValidationMessage,
  isCadenceSectionValid,
  isContentSectionValid,
} from './components/cycle-form-validation';
import {
  DAILY_CADENCE_DAYS,
  resolveDailyUiFrequency,
} from './components/cadence-frequency';
import {isGoalsReflectionQuestion} from './components/goals-content';

const steps = ['Basics', 'Content', 'Cadence', 'Review'];

const footerButtonStyle = {
  boxSizing: 'border-box' as const,
  height: '44px',
  padding: '10px 16px',
};

const disabledFooterButtonStyle = {
  ...footerButtonStyle,
  backgroundColor: 'var(--beam-surface-muted)',
  borderColor: 'var(--beam-surface-muted)',
  color: 'var(--beam-text-muted)',
};

const cyclePageBodyStyle = {
  padding: 0,
  margin: 0,
  backgroundColor: 'var(--beam-canvas)',
};

const mapToOptions = (
  items: Array<{
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }> = [],
) =>
  items.map((item) => ({
    value: item.id,
    label:
      item.name ||
      [item.firstName, item.lastName].filter(Boolean).join(' ') ||
      item.email ||
      '',
  }));

const initialCadenceState: CadenceFormState = {
  frequency: '',
  selectedDay: 'Mon',
  selectedDays: [],
  selectedTime: {
    time: '',
    period: 'AM',
    error: '',
  },
  timezone: 'user',
  startOption: 'immediately',
  startDate: '',
  endOption: 'never',
  occurrences: '',
  endDate: '',
  monthlyOption: null,
  monthlyNthSelection: {
    position: '',
    weekday: '',
  },
  monthlySpecificDay: '',
  oneTimeDate: '',
};

const UI_ONE_TIME_FREQUENCY = 'one-time';
const API_ONE_TIME_FREQUENCIES = ['once-for-workspace', 'once-per-workspace'];

const normalizeCycleFrequency = (frequency?: string) => {
  if (frequency && API_ONE_TIME_FREQUENCIES.includes(frequency)) {
    return UI_ONE_TIME_FREQUENCY;
  }

  return frequency || 'weekly';
};

const DAY_NAME_TO_ABBR: Record<string, DayOfWeek> = {
  sunday: 'Sun',
  sun: 'Sun',
  monday: 'Mon',
  mon: 'Mon',
  tuesday: 'Tue',
  tue: 'Tue',
  wednesday: 'Wed',
  wed: 'Wed',
  thursday: 'Thu',
  thu: 'Thu',
  friday: 'Fri',
  fri: 'Fri',
  saturday: 'Sat',
  sat: 'Sat',
};

const toDayAbbreviation = (value?: string): DayOfWeek => {
  if (!value) return 'Mon';
  const normalized = value.toLowerCase();
  return DAY_NAME_TO_ABBR[normalized] || 'Mon';
};

const convertSendTimeTo12Hour = (
  sendTime?: string,
): CadenceFormState['selectedTime'] => {
  if (!sendTime) {
    return {
      time: '',
      period: 'AM',
      error: '',
    };
  }

  const [hoursPart, minutesPart = '00'] = sendTime.split(':');
  let hours = parseInt(hoursPart, 10);
  if (Number.isNaN(hours)) {
    return {
      time: sendTime,
      period: 'AM',
      error: '',
    };
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
  return {
    time: `${String(normalizedHour).padStart(2, '0')}:${minutesPart}`,
    period: period as 'AM' | 'PM',
    error: '',
  };
};

const mapIdsToOptions = (
  ids: string[] = [],
  options: SelectOption[],
): SelectOption[] => {
  const labels = new Map(options.map((option) => [option.value, option.label]));
  return ids.map((id) => ({
    value: id,
    label: labels.get(id) || id,
  }));
};

export const buildBlocksFromCycle = (
  cycle: ICheckinCycleDetail,
): ContentBlock[] => {
  const questionBlocks: ContentBlock[] =
    cycle.content?.questions?.map((question, index) =>
      isGoalsReflectionQuestion(question?.question)
        ? {
            id: `goals-${index}`,
            type: 'goals' as const,
          }
        : {
            id: `question-${index}`,
            type: 'question' as const,
            questionData: {
              type: question?.questionId ? 'template' : 'custom',
              text: question?.question,
              optional: !(question?.required ?? true),
              answerType: question?.answerType,
              questionId: question?.questionId
                ? String(question.questionId)
                : undefined,
              source: question?.questionId
                ? ('template' as const)
                : ('custom' as const),
              templateCategory: question?.questionId
                ? 'My questions'
                : undefined,
            },
          },
    ) || [];

  const checklistBlocks =
    cycle.content?.priorities?.map((priority, index) => ({
      id: `checklist-${index}`,
      type: 'checklist' as const,
      checklistData: {
        title: priority?.title || 'Checklist',
        checklist:
          priority?.checklist?.map((item, itemIndex) => {
            const text =
              typeof item === 'string'
                ? item
                : (item as {text?: string})?.text || String(item);
            return {
              text,
              id: `${index}-${itemIndex}`,
            };
          }) || [],
        required: false,
      },
    })) || [];

  const pulseBlocks =
    cycle.content?.pulse?.map((pulse, index) => ({
      id: `pulse-${index}`,
      type: 'pulse' as const,
      pulseData: {
        name: pulse?.name || 'Pulse check-in',
        options: pulse?.options || [],
        emojis: pulse?.emojis || [],
        required: false,
        anonymous: cycle.visibility === 'anonymous',
      },
    })) || [];

  return [...questionBlocks, ...pulseBlocks, ...checklistBlocks];
};

export const mapCycleToFormData = (
  cycle: ICheckinCycleDetail,
  groupOptions: SelectOption[],
  peopleOptions: SelectOption[],
): CreateCheckinCycleFormData => {
  const includeScope: IncludeScope = [];
  if (cycle.includedGroups?.length) {
    includeScope.push('groups');
  }
  if (cycle.includedIndividuals?.length) {
    includeScope.push('individuals');
  }
  if (includeScope.length === 0) {
    includeScope.push('everyone');
  }

  const excludeScope: ExcludeScope = [];
  if (cycle.excludedGroups?.length) {
    excludeScope.push('groups');
  }
  if (cycle.excludedIndividuals?.length) {
    excludeScope.push('individuals');
  }

  const cadenceConfig = cycle.config || ({} as ICheckinCycleDetail['config']);
  const normalizedFrequency = normalizeCycleFrequency(cadenceConfig?.frequency);

  const configuredDays =
    cadenceConfig?.dayOfWeek && normalizedFrequency === 'daily'
      ? cadenceConfig.dayOfWeek.map((day) => toDayAbbreviation(day))
      : [];
  const cadenceFrequency = resolveDailyUiFrequency(
    normalizedFrequency,
    configuredDays,
  );
  const isOneTime = cadenceFrequency === UI_ONE_TIME_FREQUENCY;

  const selectedDay = cadenceConfig?.dayOfWeek?.[0]
    ? toDayAbbreviation(cadenceConfig.dayOfWeek[0])
    : 'Mon';

  const selectedDays =
    cadenceFrequency === 'daily' ? [...DAILY_CADENCE_DAYS] : configuredDays;

  const selectedTime = convertSendTimeTo12Hour(cadenceConfig?.sendTime);

  const cadence: CadenceFormState = {
    frequency: cadenceFrequency,
    selectedDay,
    selectedDays,
    selectedTime,
    timezone: cadenceConfig?.timezone === 'workspace' ? 'workspace' : 'user',
    startOption:
      !isOneTime && cadenceConfig?.startDate ? 'specific' : 'immediately',
    startDate:
      !isOneTime && cadenceConfig?.startDate
        ? dayjs(cadenceConfig.startDate).format('YYYY-MM-DD')
        : '',
    endOption: cadenceConfig?.endDate
      ? 'specific'
      : cadenceConfig?.endsAfterOccurrences
      ? 'after'
      : 'never',
    occurrences: cadenceConfig?.endsAfterOccurrences
      ? String(cadenceConfig.endsAfterOccurrences)
      : '',
    endDate: cadenceConfig?.endDate
      ? dayjs(cadenceConfig.endDate).format('YYYY-MM-DD')
      : '',
    monthlyOption: cadenceFrequency === 'monthly' ? 'specificDay' : null,
    monthlyNthSelection: {
      position: '',
      weekday: '',
    },
    monthlySpecificDay: cadenceConfig?.dayOfMonth
      ? String(cadenceConfig.dayOfMonth)
      : '',
    oneTimeDate:
      isOneTime && cadenceConfig?.startDate
        ? dayjs(cadenceConfig.startDate).format('YYYY-MM-DD')
        : '',
  };

  return {
    cycleName: cycle.cycleName || '',
    includeScope,
    includeGroups: includeScope.includes('groups')
      ? mapIdsToOptions(cycle.includedGroups, groupOptions)
      : [],
    includePeople: includeScope.includes('individuals')
      ? mapIdsToOptions(cycle.includedIndividuals, peopleOptions)
      : [],
    excludeScope,
    excludeGroups: excludeScope.includes('groups')
      ? mapIdsToOptions(cycle.excludedGroups, groupOptions)
      : [],
    excludePeople: excludeScope.includes('individuals')
      ? mapIdsToOptions(cycle.excludedIndividuals, peopleOptions)
      : [],
    visibility: (cycle.visibility || 'private') as VisibilityOption,
    contentBlocks: buildBlocksFromCycle(cycle),
    cadence,
  };
};

const Component = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    groupStore: {allGroups, setAllGroups},
    usersStore: {users},
  } = useStoreContext();
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [hasLoadedAudience, setHasLoadedAudience] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const initGroups = useCallback(async () => {
    setIsLoadingGroups(true);
    setHasLoadedAudience(false);
    setAudienceError(null);
    try {
      const grpTypeRequest = new groupTypeRequestImpl();
      const grpTypeCtrl = new groupTypeController(grpTypeRequest);
      const grpRequest = new groupRequestImpl();
      const grpCtrl = new groupController(grpRequest);
      const grpTypeResponse: any = await grpTypeCtrl.newfetchGroupType();
      if (!Array.isArray(grpTypeResponse)) {
        throw new Error('Unable to load groups and teams.');
      }
      await Promise.all(
        grpTypeResponse.map(async (grpType: any) => {
          const grpResponse: any = await grpCtrl.newfetchGroup(grpType.id);
          if (grpResponse?.groups) {
            setAllGroups(grpResponse.groups, grpType.name);
          }
        }),
      );
    } catch {
      setAudienceError('Unable to load groups and teams.');
    } finally {
      setIsLoadingGroups(false);
      setHasLoadedAudience(true);
    }
  }, [setAllGroups]);

  useEffect(() => {
    initGroups();
  }, [initGroups]);

  const checkinsRequest = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const checkinsController = useMemo(
    () => new CompanyCheckinsController(checkinsRequest),
    [checkinsRequest],
  );
  const previewCycleId = searchParams.get('cycleId');
  const shouldPreviewCycle = searchParams.get('preview') === 'true';

  const {data: previewCycle, isLoading: isLoadingPreviewCycle} = useQuery<
    ICheckinCycleDetail | false
  >(
    ['check-in-cycle', previewCycleId],
    () => checkinsController.getCheckinCycle(previewCycleId as string),
    {
      enabled: Boolean(previewCycleId) && shouldPreviewCycle,
      refetchOnWindowFocus: false,
    },
  );

  const [activeStep, setActiveStep] = useState(1);
  const [includeScope, setIncludeScope] = useState<IncludeScope>([]);
  const [excludeScope, setExcludeScope] = useState<ExcludeScope>([]);
  const [includeGroups, setIncludeGroups] = useState<SelectOption[]>([]);
  const [includePeople, setIncludePeople] = useState<SelectOption[]>([]);
  const [excludeGroups, setExcludeGroups] = useState<SelectOption[]>([]);
  const [excludePeople, setExcludePeople] = useState<SelectOption[]>([]);
  const [visibilitySetting, setVisibilitySetting] = useState<VisibilityOption>(
    'public',
  );
  const [cycleName, setCycleName] = useState('');
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [cadence, setCadence] = useState<CadenceFormState>(initialCadenceState);
  const [hydratedDraftId, setHydratedDraftId] = useState<string | null>(null);
  const [
    sectionEditSnapshot,
    setSectionEditSnapshot,
  ] = useState<CreateCheckinCycleFormData | null>(null);

  const groupOptions: SelectOption[] = useMemo(() => {
    return Array.from(allGroups.values()).map((group: any) => ({
      value: group.id,
      label: group.name || group.groupTypeName || 'Unnamed group',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups, allGroups.size]);

  const peopleOptions = useMemo(() => mapToOptions((users as any) || []), [
    users,
  ]);
  const previewFormData = useMemo(() => {
    if (!previewCycle) {
      return null;
    }
    return mapCycleToFormData(previewCycle, groupOptions, peopleOptions);
  }, [previewCycle, groupOptions, peopleOptions]);
  const isDraftEdit = Boolean(
    shouldPreviewCycle &&
      previewCycleId &&
      previewCycle &&
      previewCycle.status === 'draft',
  );
  const hasHydratedDraft = Boolean(
    previewCycleId && hydratedDraftId === previewCycleId,
  );

  const updateIncludeGroups = (options: SelectOption[]) =>
    setIncludeGroups(options);
  const updateIncludePeople = (options: SelectOption[]) =>
    setIncludePeople(options);
  const updateExcludeGroups = (options: SelectOption[]) =>
    setExcludeGroups(options);
  const updateExcludePeople = (options: SelectOption[]) =>
    setExcludePeople(options);

  const formData: CreateCheckinCycleFormData = {
    cycleName,
    includeScope,
    includeGroups,
    includePeople,
    excludeScope,
    excludeGroups,
    excludePeople,
    visibility: visibilitySetting,
    contentBlocks,
    cadence,
  };

  const basicsValidationMessage = getBasicsValidationMessage(formData);
  const basicsValid = basicsValidationMessage === null;
  const contentValid = isContentSectionValid(formData);
  const visibilityValid = visibilitySetting !== '';
  const cadenceValid = isCadenceSectionValid(formData);

  const applyFormData = useCallback((data: CreateCheckinCycleFormData) => {
    const next = cloneFormData(data);
    setIncludeScope(next.includeScope);
    setExcludeScope(next.excludeScope);
    setIncludeGroups(next.includeGroups);
    setIncludePeople(next.includePeople);
    setExcludeGroups(next.excludeGroups);
    setExcludePeople(next.excludePeople);
    setVisibilitySetting(next.visibility);
    setCycleName(next.cycleName);
    setContentBlocks(next.contentBlocks);
    setCadence(next.cadence);
  }, []);

  useEffect(() => {
    if (
      !shouldPreviewCycle ||
      !previewCycleId ||
      !previewCycle ||
      previewCycle.status === 'draft'
    ) {
      return;
    }

    navigate(
      `/checkins/team-management/cycles/${previewCycleId}?tab=settings`,
      {replace: true},
    );
  }, [navigate, previewCycle, previewCycleId, shouldPreviewCycle]);

  useEffect(() => {
    if (
      !isDraftEdit ||
      !previewFormData ||
      !hasLoadedAudience ||
      hasHydratedDraft
    ) {
      return;
    }

    applyFormData(previewFormData);
    setActiveStep(1);
    setHydratedDraftId(previewCycleId);
  }, [
    applyFormData,
    hasHydratedDraft,
    hasLoadedAudience,
    isDraftEdit,
    previewCycleId,
    previewFormData,
  ]);

  const stepForSection = (section: PreviewSection) => {
    const stepMap: Record<PreviewSection, number> = {
      participants: 1,
      visibility: 2,
      content: 2,
      cadence: 3,
    };
    return stepMap[section];
  };

  const beginSectionEdit = (section: PreviewSection) => {
    setSectionEditSnapshot(cloneFormData(formData));
    setActiveStep(stepForSection(section));
  };

  const sectionHasChanges = Boolean(
    sectionEditSnapshot && hasFormDataChanged(formData, sectionEditSnapshot),
  );

  const activeSectionValid =
    (activeStep === 1 && basicsValid) ||
    (activeStep === 2 && visibilityValid && contentValid) ||
    (activeStep === 3 && cadenceValid);

  const cancelSectionEdit = () => {
    if (sectionEditSnapshot) {
      applyFormData(sectionEditSnapshot);
    }
    setSectionEditSnapshot(null);
    setActiveStep(4);
  };

  const saveSectionEdit = () => {
    if (!sectionHasChanges || !activeSectionValid) {
      return;
    }
    setSectionEditSnapshot(null);
    setActiveStep(4);
  };

  const returnToCadenceStep = () => {
    setSectionEditSnapshot(null);
    setActiveStep(3);
  };

  if (shouldPreviewCycle && previewCycleId) {
    const isRedirectingToDetail = Boolean(
      previewCycle && previewCycle.status !== 'draft',
    );
    if (
      isLoadingPreviewCycle ||
      (!hasHydratedDraft && !hasLoadedAudience) ||
      isRedirectingToDetail ||
      (isDraftEdit && !hasHydratedDraft)
    ) {
      return (
        <PageLayoutTemplate
          title="Edit Check-in Cycle"
          onClick={() => navigate(-1)}
          bodyStyle={cyclePageBodyStyle}>
          <div className="flex justify-center items-center py-20">
            <ItemLoader />
          </div>
        </PageLayoutTemplate>
      );
    }

    if (!previewCycle) {
      return (
        <PageLayoutTemplate
          title="Edit Check-in Cycle"
          onClick={() => navigate('/checkins/team-management')}
          bodyStyle={cyclePageBodyStyle}>
          <div className="flex flex-col items-center gap-4 py-20">
            <p className="text-sm text-textBody">
              This draft cycle is not available.
            </p>
            <Button
              kind="secondary"
              onClick={() => navigate('/checkins/team-management')}>
              Back to Team Management
            </Button>
          </div>
        </PageLayoutTemplate>
      );
    }
  }

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return (
          <BasicsStep
            cycleName={cycleName}
            includeScope={includeScope}
            excludeScope={excludeScope}
            includeGroups={includeGroups}
            includePeople={includePeople}
            excludeGroups={excludeGroups}
            excludePeople={excludePeople}
            groupOptions={groupOptions}
            peopleOptions={peopleOptions}
            isLoadingGroups={isLoadingGroups}
            audienceError={audienceError}
            onRetryAudience={initGroups}
            onCycleNameChange={setCycleName}
            onIncludeScopeChange={setIncludeScope}
            onExcludeScopeChange={setExcludeScope}
            onIncludeGroupsChange={updateIncludeGroups}
            onIncludePeopleChange={updateIncludePeople}
            onExcludeGroupsChange={updateExcludeGroups}
            onExcludePeopleChange={updateExcludePeople}
          />
        );
      case 2:
        return (
          <ContentStep
            blocks={contentBlocks}
            onBlocksChange={setContentBlocks}
            showValidation={false}
            visibility={visibilitySetting}
            onVisibilityChange={setVisibilitySetting}
          />
        );
      case 3:
        return <CadenceStep value={cadence} onChange={setCadence} />;
      default:
        return null;
    }
  };

  const handleContinue = () => {
    if (activeStep === 1 && !basicsValid) {
      return;
    }
    if (activeStep === 2 && (!visibilityValid || !contentValid)) {
      return;
    }
    if (activeStep === 3 && !cadenceValid) {
      return;
    }
    if (activeStep < 4) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const isContinueDisabled =
    (activeStep === 1 && !basicsValid) ||
    (activeStep === 2 && (!visibilityValid || !contentValid)) ||
    (activeStep === 3 && !cadenceValid);

  if (activeStep === 4) {
    const reviewHasChanges = Boolean(
      !isDraftEdit ||
        !previewFormData ||
        hasFormDataChanged(formData, previewFormData),
    );

    return (
      <PreviewStep
        data={formData}
        mode={isDraftEdit ? 'edit' : 'create'}
        cycleId={isDraftEdit ? previewCycleId || undefined : undefined}
        statusOverride={
          isDraftEdit && previewCycle ? previewCycle.status : undefined
        }
        hasChanges={reviewHasChanges}
        showWizardProgress
        onWizardStepChange={(step) => {
          const sectionByStep: Record<number, PreviewSection> = {
            1: 'participants',
            2: 'content',
            3: 'cadence',
          };
          if (sectionByStep[step]) {
            beginSectionEdit(sectionByStep[step]);
          }
        }}
        onClose={() => navigate(-1)}
        onComplete={() =>
          navigate('/checkins/team-management', {replace: true})
        }
        onBack={returnToCadenceStep}
        onEditSection={beginSectionEdit}
      />
    );
  }

  return (
    <PageLayoutTemplate
      title={
        sectionEditSnapshot
          ? cycleName || 'Edit Check-in Cycle'
          : isDraftEdit
          ? 'Edit Check-in Cycle'
          : 'Create Check-in Cycle'
      }
      onClick={() => {
        if (sectionEditSnapshot) {
          cancelSectionEdit();
        } else {
          navigate(-1);
        }
      }}
      bodyStyle={cyclePageBodyStyle}>
      <div className="bg-white">
        <WizardSteps
          steps={steps}
          activeStep={activeStep}
          onChange={sectionEditSnapshot ? undefined : setActiveStep}
        />
      </div>

      <div>
        <div
          className={`mx-auto flex w-full flex-col gap-6 px-4 pt-10 pb-0 ${
            activeStep === 2 ? 'max-w-[686px]' : 'max-w-[576px]'
          }`}>
          {sectionEditSnapshot && sectionHasChanges && (
            <div
              role="status"
              aria-live="polite"
              className="w-fit rounded-lg border border-dashed border-red400 px-3 py-2 text-xs font-medium leading-5 text-red400">
              Unsaved changes
            </div>
          )}
          {renderStep()}
        </div>

        <div
          className={`sticky bottom-0 mx-auto flex w-full items-center bg-backgroundLight px-4 py-6 ${
            sectionEditSnapshot
              ? activeStep === 2
                ? 'max-w-[686px] justify-between'
                : 'max-w-[576px] justify-between'
              : activeStep === 2
              ? 'max-w-[686px] justify-between'
              : activeStep > 1
              ? 'max-w-[576px] justify-between'
              : 'max-w-[576px]'
          }`}>
          {sectionEditSnapshot ? (
            <>
              <Button
                kind="secondary"
                type="button"
                width="xs"
                style={footerButtonStyle}
                onClick={cancelSectionEdit}>
                Cancel
              </Button>
              <Button
                type="button"
                width="xs"
                disabled={!sectionHasChanges || !activeSectionValid}
                style={
                  !sectionHasChanges || !activeSectionValid
                    ? disabledFooterButtonStyle
                    : footerButtonStyle
                }
                onClick={saveSectionEdit}>
                Save changes
              </Button>
            </>
          ) : (
            <>
              {activeStep > 1 && (
                <Button
                  kind="secondary"
                  width="xs"
                  style={footerButtonStyle}
                  onClick={() => setActiveStep(activeStep - 1)}>
                  <span className="flex items-center gap-2.5">
                    <ArrowLeftIcon />
                    Go back
                  </span>
                </Button>
              )}
              <div className={activeStep === 1 ? 'w-full' : ''}>
                {activeStep === 1 && basicsValidationMessage && (
                  <p
                    id="basics-continue-hint"
                    role="status"
                    className="mb-2 text-sm leading-5 text-textBody">
                    {basicsValidationMessage}
                  </p>
                )}
                <Button
                  width={activeStep === 1 ? 'full' : 'xs'}
                  disabled={isContinueDisabled}
                  aria-describedby={
                    activeStep === 1 && basicsValidationMessage
                      ? 'basics-continue-hint'
                      : undefined
                  }
                  style={
                    isContinueDisabled
                      ? disabledFooterButtonStyle
                      : footerButtonStyle
                  }
                  onClick={handleContinue}>
                  Continue
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayoutTemplate>
  );
});

export const CreateCheckinCyclePage = Component;
export default Component;
